'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import type { Prompt } from '@/types/prompt.types'
import type { Comment } from '@/types/comment.types'
import type { ActionResult } from '@/types/actions'
import type { StarPromptResult, ForkPromptResult } from '@/types/prompt.types'
import type { CreateCommentResult } from '@/types/comment.types'
import { useCommentThread } from '@/hooks/use-comment-thread'
import { useVersionHistory } from '@/hooks/use-version-history'
import { formatDate, formatNumber } from '@/lib/utils/format'
import { extractVariables } from '@/lib/vault/prompts/variables'
import { StarButton } from './star-button'
import { ForkButton } from './fork-button'
import { ShareButtons } from '@/components/social/share-buttons'
import { ForkDialog } from './fork-dialog'
import { CommentThread } from './comment-thread'
import { CommentForm } from './comment-form'
import { ContentReviews } from '@/components/ratings/content-reviews'
import { VersionTimeline } from './version-timeline'
import { DiffView } from './diff-view'
import type { PromptVersion } from '@/types/prompt.types'

interface PromptDetailPageProps {
  prompt: Prompt
  isOwner: boolean
  isStarred: boolean
  currentUserId?: string
  currentUserProfile?: { username: string; avatarUrl: string | null }
  initialComments?: Comment[]
  starPrompt: (promptId: string) => Promise<ActionResult<StarPromptResult>>
  forkPrompt: (fd: FormData) => Promise<ActionResult<ForkPromptResult>>
  createComment: (fd: FormData) => Promise<ActionResult<CreateCommentResult>>
  forkedFrom?: { username: string; slug: string; title: string } | null
}

export function PromptDetailPage({
  prompt,
  isOwner,
  isStarred,
  currentUserId,
  currentUserProfile,
  initialComments,
  starPrompt,
  forkPrompt,
  createComment,
  forkedFrom,
}: PromptDetailPageProps) {
  const router = useRouter()
  const [showForkDialog, setShowForkDialog] = useState(false)
  const [liveForkCount, setLiveForkCount] = useState(prompt.fork_count)
  const [activeTab, setActiveTab] = useState<'content' | 'versions'>('content')
  const [selectedVersions, setSelectedVersions] = useState<{
    old: PromptVersion | null
    new: PromptVersion | null
  }>({ old: null, new: null })

  const { comments, addComment, isAdding } = useCommentThread({
    promptId: prompt.id,
    initialData: initialComments,
    currentUser: currentUserId && currentUserProfile
      ? { id: currentUserId, username: currentUserProfile.username, avatarUrl: currentUserProfile.avatarUrl }
      : undefined,
  })

  // Derive live comment count from fetched data (recursive to count replies)
  function countComments(list: Comment[]): number {
    return list.reduce((sum, c) => sum + 1 + countComments(c.replies ?? []), 0)
  }
  const liveCommentCount = countComments(comments)

  const { data: versions } = useVersionHistory({
    promptId: prompt.id,
    initialData: prompt.prompt_versions,
  })

  const [forkError, setForkError] = useState<string | null>(null)

  const handleFork = async () => {
    setForkError(null)
    const fd = new FormData()
    fd.set('promptId', prompt.id)
    fd.set('forkType', 'independent')
    const result = await forkPrompt(fd)
    if (result.success && result.data && currentUserProfile?.username) {
      setLiveForkCount((c) => c + 1)
      // SPA navigation (was window.location.href = …, a full reload). The agent
      // fork path already uses router.push; the prompt path was never migrated.
      router.push(`/p/${currentUserProfile.username}/${result.data.slug}/edit`)
      return
    }
    setForkError(!result.success ? result.error : 'Fork failed. Please log in and try again.')
  }

  const handleReply = async (content: string, parentCommentId: string) => {
    await addComment({ content, parentCommentId, createComment })
  }

  const handleAddComment = async (content: string) => {
    await addComment({ content, createComment })
  }

  // "How to use" (Faza 0 spec 0.8): 2–3 sentences generated from the prompt's
  // variables, rendered in HTML (not behind a click) so crawlers see it.
  const howToVars = extractVariables(prompt.prompt_content ?? '')
  const howToUse = (() => {
    const parts: string[] = []
    if (howToVars.length > 0) {
      const shown = howToVars.slice(0, 3).map((v) => `{{${v}}}`).join(', ')
      parts.push(`Replace ${shown} with your own details before running the prompt.`)
    } else {
      parts.push('Copy the prompt and paste it into your AI tool of choice.')
    }
    const model = (prompt as { ai_model?: string }).ai_model?.split(',')[0]?.trim()
    if (model) parts.push(`It works best with ${model}.`)
    if (prompt.category) {
      parts.push(`See more ${prompt.category.replace(/-/g, ' ')} prompts for related workflows.`)
    }
    return parts.join(' ')
  })()

  return (
    <article className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      {/* Header */}
      <header className="mb-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <h1 className="text-[var(--text-3xl)] font-bold text-[var(--color-text-primary)]">
              {prompt.title}
            </h1>
            <p className="mt-1 text-[var(--text-sm)] text-[var(--color-text-secondary)]">
              By{' '}
              <Link
                href={`/u/${prompt.username_ref}`}
                // underline so the link is distinguishable inside the text
                // block without relying on color alone (axe link-in-text-block,
                // WCAG 1.4.1).
                className="underline text-[var(--color-brand-indigo)] hover:text-[var(--color-brand-violet)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-indigo)]"
              >
                @{prompt.username_ref}
              </Link>
              {' '}&middot;{' '}
              <time dateTime={prompt.created_at}>
                {formatDate(prompt.created_at)}
              </time>
            </p>
            {forkedFrom && (
              <p className="mt-1 text-[var(--text-sm)] text-[var(--color-text-muted)]">
                Forked from{' '}
                <Link
                  href={`/p/${forkedFrom.username}/${forkedFrom.slug}`}
                  className="underline text-[var(--color-brand-indigo)] hover:text-[var(--color-brand-violet)]"
                >
                  @{forkedFrom.username}/{forkedFrom.title}
                </Link>
              </p>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            <StarButton
              promptId={prompt.id}
              isStarred={isStarred}
              count={prompt.star_count}
              starPrompt={starPrompt}
            />
            <ForkButton
              promptId={prompt.id}
              isOwner={isOwner}
              forkCount={liveForkCount}
              onFork={() => setShowForkDialog(true)}
            />
            {isOwner && (
              <Link
                href={`/p/${prompt.username_ref}/${prompt.slug}/edit`}
                className="rounded-[var(--radius-md)] border border-[var(--color-border-default)] px-3 py-1.5 text-[var(--text-sm)] text-[var(--color-text-secondary)] transition-colors hover:border-[var(--color-brand-indigo)] hover:text-[var(--color-text-primary)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-indigo)]"
              >
                Edit
              </Link>
            )}
          </div>
        </div>

        {/* Share */}
        <ShareButtons
          className="mt-3 flex flex-wrap items-center"
          url={`${typeof window === 'undefined' ? '' : window.location.origin}/p/${prompt.username_ref}/${prompt.slug}`}
          title={prompt.title}
          entityType="prompt"
          entityId={prompt.id}
        />

        {/* Stats */}
        <div className="mt-4 flex flex-wrap items-center gap-4 text-[var(--text-sm)] text-[var(--color-text-muted)]">
          {prompt.rating_count > 0 && (
            <span
              aria-label={`${prompt.rating.toFixed(1)} stars from ${prompt.rating_count} review${prompt.rating_count === 1 ? '' : 's'}`}
              className="flex items-center gap-1 tabular-nums"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="var(--color-brand-amber)" stroke="var(--color-brand-amber)" strokeWidth="2" aria-hidden="true">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              {prompt.rating.toFixed(1)} ({formatNumber(prompt.rating_count)})
            </span>
          )}
          <span aria-label={`${prompt.star_count} ${prompt.star_count === 1 ? 'like' : 'likes'}`} className="tabular-nums">
            {formatNumber(prompt.star_count)} {prompt.star_count === 1 ? 'like' : 'likes'}
          </span>
          <span aria-label={`${liveForkCount} ${liveForkCount === 1 ? 'fork' : 'forks'}`} className="tabular-nums">
            {formatNumber(liveForkCount)} {liveForkCount === 1 ? 'fork' : 'forks'}
          </span>
          <span aria-label={`${liveCommentCount} ${liveCommentCount === 1 ? 'comment' : 'comments'}`} className="tabular-nums">
            {formatNumber(liveCommentCount)} {liveCommentCount === 1 ? 'comment' : 'comments'}
          </span>
          {prompt.prompt_tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {prompt.prompt_tags.map((t) => (
                <span
                  key={t.tag}
                  className="rounded-[var(--radius-full)] bg-[var(--color-bg-elevated)] px-2 py-0.5 text-[var(--text-xs)]"
                >
                  {t.tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Tab navigation */}
      <nav className="mb-6 flex border-b border-[var(--color-border-default)]" role="tablist">
        <button
          role="tab"
          aria-selected={activeTab === 'content'}
          onClick={() => setActiveTab('content')}
          className={`border-b-2 px-4 py-2.5 text-[var(--text-sm)] font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-indigo)] ${
            activeTab === 'content'
              ? 'border-[var(--color-brand-indigo)] text-[var(--color-text-primary)]'
              : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
          }`}
        >
          Content
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'versions'}
          onClick={() => setActiveTab('versions')}
          className={`border-b-2 px-4 py-2.5 text-[var(--text-sm)] font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-indigo)] ${
            activeTab === 'versions'
              ? 'border-[var(--color-brand-indigo)] text-[var(--color-text-primary)]'
              : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
          }`}
        >
          Versions{versions && versions.length > 0 ? ` (${versions.length})` : ''}
        </button>
      </nav>

      {/* Main content */}
      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="min-w-0 flex-1">
          {activeTab === 'content' && (
            <div role="tabpanel" aria-label="Prompt content">
              {/* Description */}
              {prompt.readme_content && (
                <section className="mb-6">
                  <h2 className="sr-only">Description</h2>
                  <div className="prose-dark rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-4 text-[var(--text-sm)] leading-relaxed text-[var(--color-text-secondary)]">
                    {prompt.readme_content}
                  </div>
                </section>
              )}

              {/* Prompt content */}
              <section>
                <h2 className="mb-3 text-[var(--text-sm)] font-semibold text-[var(--color-text-primary)]">
                  Prompt
                </h2>
                <pre className="overflow-x-auto whitespace-pre-wrap rounded-[var(--radius-md)] border border-[var(--code-border)] bg-[var(--code-bg)] p-4 font-mono text-[var(--text-sm)] leading-relaxed text-[var(--code-text)]">
                  {prompt.prompt_content.replace(/<[^>]*>/g, '')}
                </pre>
              </section>

              {/* How to use (0.8) */}
              <section className="mt-6">
                <h2 className="mb-3 text-[var(--text-sm)] font-semibold text-[var(--color-text-primary)]">
                  How to use
                </h2>
                <div className="rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-4 text-[var(--text-sm)] leading-relaxed text-[var(--color-text-secondary)]">
                  <p>{howToUse}</p>
                  {prompt.category && (
                    <p className="mt-2">
                      <Link
                        href={`/prompts/${prompt.category}`}
                        className="text-[var(--color-brand-indigo)] underline hover:opacity-80"
                      >
                        Browse {prompt.category.replace(/-/g, ' ')} prompts &rarr;
                      </Link>
                    </p>
                  )}
                </div>
              </section>

              {/* Reviews */}
              <section className="mt-8" id="reviews">
                <h2 className="mb-4 text-[var(--text-lg)] font-semibold text-[var(--color-text-primary)]">
                  Reviews
                </h2>
                <ContentReviews
                  contentType="prompt"
                  contentId={prompt.id}
                  isOwner={isOwner}
                  typeLabel="prompt"
                />
              </section>

              {/* Comments */}
              <section className="mt-8" id="comments">
                <h2 className="mb-4 text-[var(--text-lg)] font-semibold text-[var(--color-text-primary)]">
                  Comments ({liveCommentCount})
                </h2>
                <CommentForm
                  onSubmit={handleAddComment}
                  disabled={!currentUserId || isAdding}
                  avatarUrl={currentUserProfile?.avatarUrl}
                  username={currentUserProfile?.username}
                />
                <CommentThread
                  comments={comments}
                  onReply={handleReply}
                  currentUserId={currentUserId}
                />
              </section>
            </div>
          )}

          {activeTab === 'versions' && (
            <div role="tabpanel" aria-label="Version history">
              <div className="flex flex-col gap-6 lg:flex-row">
                <div className="w-full lg:w-72">
                  <VersionTimeline
                    versions={versions ?? []}
                    currentVersionId={prompt.current_version_id}
                    onSelect={(v) =>
                      setSelectedVersions((prev) =>
                        prev.old ? { old: prev.old, new: v } : { old: v, new: null },
                      )
                    }
                    onRestore={() => {
                      /* Server action call handled by parent page */
                    }}
                  />
                </div>
                {selectedVersions.old && selectedVersions.new && (
                  <div className="min-w-0 flex-1">
                    <DiffView
                      oldVersion={selectedVersions.old}
                      newVersion={selectedVersions.new}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar metadata */}
        <aside className="w-full lg:w-72">
          <div className="sticky top-6 space-y-4 rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-4">
            <div>
              <h3 className="text-[var(--text-xs)] font-medium uppercase tracking-wider text-[var(--color-text-muted)]">
                AI Model
              </h3>
              <p className="mt-1 text-[var(--text-sm)] text-[var(--color-text-primary)]">
                {prompt.ai_model || 'Not specified'}
              </p>
            </div>
            <div>
              <h3 className="text-[var(--text-xs)] font-medium uppercase tracking-wider text-[var(--color-text-muted)]">
                Category
              </h3>
              <p className="mt-1 text-[var(--text-sm)] capitalize text-[var(--color-text-primary)]">
                {prompt.category}
              </p>
            </div>
            <div>
              <h3 className="text-[var(--text-xs)] font-medium uppercase tracking-wider text-[var(--color-text-muted)]">
                Visibility
              </h3>
              <p className="mt-1 text-[var(--text-sm)] capitalize text-[var(--color-text-primary)]">
                {prompt.visibility}
              </p>
            </div>
            <div>
              <h3 className="text-[var(--text-xs)] font-medium uppercase tracking-wider text-[var(--color-text-muted)]">
                Last Updated
              </h3>
              <time
                dateTime={prompt.updated_at}
                className="mt-1 block text-[var(--text-sm)] text-[var(--color-text-primary)]"
              >
                {formatDate(prompt.updated_at)}
              </time>
            </div>
            <div>
              <h3 className="text-[var(--text-xs)] font-medium uppercase tracking-wider text-[var(--color-text-muted)]">
                Variables
              </h3>
              {(() => {
                const cleaned = prompt.prompt_content.replace(/<[^>]*>/g, '')
                const vars = extractVariables(cleaned)
                // VARS: surface the same hint the editor shows when the author
                // wrote `{{double-brace}}` (treated as an include, not a variable,
                // so it silently shows no variable on the read view otherwise).
                const misused = [...new Set(
                  (cleaned.match(/\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g) ?? []).map((m) => m.slice(2, -2)),
                )]
                return (
                  <>
                    {vars.length > 0 ? (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {vars.map((v) => (
                          <span
                            key={v}
                            className="rounded-[var(--radius-full)] bg-[var(--color-bg-elevated)] px-2 py-0.5 text-[var(--text-xs)]"
                          >
                            {`{${v}}`}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-1 text-[var(--text-sm)] text-[var(--color-text-primary)]">None</p>
                    )}
                    {misused.length > 0 && (
                      <p className="mt-1.5 text-[var(--text-xs)] text-[var(--color-text-muted)]">
                        Tip:{' '}
                        <code className="rounded bg-[var(--color-bg-elevated)] px-1">{`{${misused[0]}}`}</code>{' '}
                        (single braces) marks a variable;{' '}
                        <code className="rounded bg-[var(--color-bg-elevated)] px-1">{`{{${misused[0]}}}`}</code>{' '}
                        is read as an include, not a variable.
                      </p>
                    )}
                  </>
                )
              })()}
            </div>
          </div>
        </aside>
      </div>

      {/* Fork dialog */}
      <ForkDialog
        isOpen={showForkDialog}
        promptTitle={prompt.title}
        authorUsername={prompt.username_ref}
        error={forkError}
        onFork={handleFork}
        onCancel={() => { setShowForkDialog(false); setForkError(null) }}
      />
    </article>
  )
}
